import {
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Tag,
  TagLabel,
  useDisclosure,
} from '@chakra-ui/react';
import {
  CellContext,
  ColumnDef,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { UploadedDocumentTypeGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import {
  GetCreditFacilityKnowledgeBaseDocumentIdDocumentGQLType,
  GetUploadedDocumentsDocumentGQLType,
  useGetUploadedDocumentsQuery,
  useUpdateDocumentToLatestPublishedMutation,
} from '../../requests/requests.gql.generated';
import { DownloadCreditFacilityDocumentButton } from './DownloadCreditFacilityDocumentButton';
import { Icon } from 'packages/common-frontend/components/icons';
import { UPLOADED_DOCUMENT_TYPE_TO_LABEL } from '~/core/routes/settings/implementations/common/types';
import { IconButtonV2 } from '~/packages/common-frontend/components/IconButton';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { TableBETA } from '~/packages/common-frontend/tables/TableBETA';
import { remToPx } from '~/packages/common-frontend/theme/utils';
import { ConfirmRenameDocumentModal } from '~/packages/credit-facility/ui/DocumentUpload/ConfirmRenameDocumentModal';

type TableData = {
  capitalProviderName: string | null;
  documentType: UploadedDocumentTypeGQLType;
  documentName: string;
  status: string;
  uploadedAt: Date;
  uploadedDocumentId: string;
};

const EditDocumentMenu = ({ row }: CellContext<TableData, any>) => {
  const [updateDocumentToLatest] = useUpdateDocumentToLatestPublishedMutation({
    variables: {
      documentId: row.original.uploadedDocumentId,
    },
    refetchQueries: [
      GetUploadedDocumentsDocumentGQLType,
      GetCreditFacilityKnowledgeBaseDocumentIdDocumentGQLType,
    ],
  });

  const renameDocumentModalControls = useDisclosure();

  return (
    <Flex justifyContent="space-between">
      <ConfirmRenameDocumentModal
        isOpen={renameDocumentModalControls.isOpen}
        onClose={renameDocumentModalControls.onClose}
        documentName={row.original.documentName}
        documentId={row.original.uploadedDocumentId}
      />

      <Flex alignItems="center">
        {row.original.status === 'latestPublished' && (
          <Tag size="sm" variant="solid-muted" colorScheme="green">
            <TagLabel>Latest</TagLabel>
          </Tag>
        )}
      </Flex>
      <Menu computePositionOnMount strategy="fixed">
        <MenuButton
          as={IconButtonV2}
          colorScheme="gray"
          icon={<Icon icon="ellipsis-vertical" />}
          variant="secondary-muted"
        />

        <MenuList zIndex="dropdown">
          <DownloadCreditFacilityDocumentButton
            uploadedDocumentId={row.original.uploadedDocumentId}
          />

          {row.original.status !== 'latestPublished' && (
            <MenuItem onClick={() => updateDocumentToLatest()}>
              Make latest
            </MenuItem>
          )}
          <MenuItem onClick={() => renameDocumentModalControls.onOpen()}>
            Rename
          </MenuItem>
        </MenuList>
      </Menu>
    </Flex>
  );
};

const columnDefs: ColumnDef<TableData, any>[] = [
  {
    accessorKey: 'documentType',
    header: 'File Type',
    cell: ({ getValue }) => {
      const uploadedDocumentType: UploadedDocumentTypeGQLType = getValue();
      return UPLOADED_DOCUMENT_TYPE_TO_LABEL[uploadedDocumentType];
    },
  },
  {
    accessorKey: 'capitalProviderName',
    header: 'Lender',
    cell: ({ getValue }) => getValue() ?? '-',
  },
  {
    accessorKey: 'uploadedAt',
    header: 'Uploaded',
    cell: ({ getValue }) => {
      // todo: add time zones @markhaghani @implementation
      return commonDate.dateToHumanDateString(
        getValue(),
        'en-us',
        'America/New_York',
        'numeric',
        'numeric',
      );
    },
  },
  // todo @finley-ai @mark - make this name editable
  {
    accessorKey: 'documentName',
    header: 'Name',
    cell: ({ getValue }) => getValue() ?? '-',
  },
  {
    id: 'editDocument',
    minSize: remToPx(1),
    size: remToPx(1),
    header: '',
    cell: EditDocumentMenu,
  },
];

export const CreditFacilityDocumentsHistoryTable = ({
  creditFacilityId,
}: {
  creditFacilityId: string;
}) => {
  const {
    data: uploadedDocumentsQuery,
    error,
    loading,
  } = useGetUploadedDocumentsQuery({
    variables: {
      creditFacilityId,
    },
  });

  const tableData = useMemo(() => {
    const uploadedDocumentsData =
      uploadedDocumentsQuery?.creditFacility?.uploadedDocuments ?? [];

    return uploadedDocumentsData.map((row) => ({
      capitalProviderName: row.capitalProvider?.name ?? null,
      documentType: row.documentType,
      documentName: row.documentName,
      status: row.status,
      uploadedAt: new Date(row.uploadedAt),
      uploadedDocumentId: row.id,
    }));
  }, [uploadedDocumentsQuery]);

  const table = useReactTable({
    data: tableData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (loading) return <Skeleton height="600px" />;

  if (tableData.length === 0) {
    return (
      <Typography variant="base-muted">
        No documents have been uploaded yet. Please upload a document above.
      </Typography>
    );
  }

  if (error) throw error;

  return <TableBETA table={table} />;
};
